/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Customizer Service - Helper functions for design saving and validation
 * Updated: 2026-01-09
 */

import {
  getFirestore,
  collection,
  doc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';

/**
 * Validate design data before saving to Firestore
 * Ensures all critical fields are present and non-empty
 */
export const validateDesignData = (designData: any): boolean => {
  const required = [
    'imageId',
    'imagePath',
    'transformedImageId',
    'transformedImagePath',
    'mockupPath',
    'templateId',
    'haravan_variant_id',
    'visitor_id',
    'session_id',
  ];

  for (const field of required) {
    if (!designData[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  // Additional checks for paths
  if (typeof designData.imagePath === 'string' && designData.imagePath.trim() === '') {
    throw new Error('imagePath cannot be empty');
  }

  if (typeof designData.imagePath === 'string' && !designData.imagePath.startsWith('/')) {
    throw new Error('imagePath must be a valid Modal path');
  }

  if (typeof designData.transformedImagePath === 'string' && designData.transformedImagePath.trim() === '') {
    throw new Error('transformedImagePath cannot be empty');
  }

  if (typeof designData.transformedImagePath === 'string' && !designData.transformedImagePath.startsWith('/')) {
    throw new Error('transformedImagePath must be a valid Modal path');
  }

  // Validate position and scale are in correct format
  if (!designData.position || typeof designData.position.x !== 'number' || typeof designData.position.y !== 'number') {
    throw new Error('position must be an object with x and y as numbers (percentage)');
  }

  if (typeof designData.scale !== 'number' || designData.scale <= 0) {
    throw new Error('scale must be a positive number (percentage)');
  }

  return true;
};

/**
 * Save design to Firestore with Firestore-first approach
 * Returns design_id for later tracking
 */
export const saveDesignToFirestore = async (designData: {
  // Original image (always saved)
  imageId: string;
  imagePath: string; // Path to original uploaded image
  
  // Transformed image (for printing)
  transformedImageId: string;
  transformedImagePath: string; // Path to color-transformed image (laser) or original (silk)
  
  // Mockup preview
  mockupPath: string;
  
  // Design parameters (all in percentage)
  position: { x: number; y: number }; // Position offset from center in % (-100 to +100)
  scale: number; // Size relative to printable area in % (0 to 200)
  rotation: number; // Rotation in degrees
  
  // Print specifications
  printType: 'laser' | 'silk';
  color: string;
  
  // Template reference
  templateId: string;
  haravan_product_id?: string;
  haravan_variant_id: string;
  
  // Session tracking
  visitor_id: string;
  session_id: string;
  
  // Optional metadata
  metadata?: {
    description?: string;
    positionDescription?: string;
    scaleDescription?: string;
    imageUsed?: string;
  };
}): Promise<string> => {
  // Double-check image paths are not empty before saving
  if (!designData.imagePath || !designData.imageId) {
    throw new Error('Original image path is missing. Image must be uploaded first.');
  }

  if (!designData.transformedImagePath || !designData.transformedImageId) {
    throw new Error('Transformed image path is missing. Image processing failed.');
  }

  if (!designData.mockupPath) {
    throw new Error('Mockup path is missing. Please generate preview first.');
  }

  // Generate human-readable design ID
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substr(2, 9).toUpperCase();
  const designId = `DESIGN_${timestamp}_${randomSuffix}`;

  const db = getFirestore();

  const docData = {
    // Metadata
    design_id: designId,
    status: 'pending_charge', // ← Billing will be processed async
    created_at: Timestamp.now(),
    updated_at: Timestamp.now(),

    // Image paths (VERIFIED to exist on Modal)
    image_id: designData.imageId,
    original_image_path: designData.imagePath, // Original uploaded image
    
    transformed_image_id: designData.transformedImageId,
    transformed_image_path: designData.transformedImagePath, // Image for printing (laser=transformed, silk=original)

    // Mockup
    mockup_image_path: designData.mockupPath,

    // Design parameters (all in percentage for consistency)
    design_options: {
      position: designData.position, // Position offset from center in % (x: horizontal, y: vertical)
      scale: designData.scale, // Size relative to printable area in %
      rotation: designData.rotation, // Rotation in degrees
      printType: designData.printType, // laser or silk
      color: designData.color, // Color specification (gold, silver, etc.)
    },

    // Template info
    template_id: designData.templateId,
    product_type: 'coffee_cup',

    // User tracking
    visitor_id: designData.visitor_id,
    session_id: designData.session_id,

    // Haravan integration
    haravan_product_id: designData.haravan_product_id || null,
    haravan_variant_id: designData.haravan_variant_id,
    haravan_order_id: null,

    // Billing info
    billing: {
      status: 'pending',
      amount: 1200,
      currency: 'VND',
      retry_count: 0,
      charge_error: null,
      charged_at: null,
      last_retry_at: null,
    },

    // Metadata for reference
    metadata: designData.metadata || {},

    // For admin tracking
    processing_attempts: [],
  };

  try {
    // Save to Firestore using design_id as document ID
    const docRef = doc(db, 'design_submissions', designId);
    await setDoc(docRef, docData);

    console.log(`✅ Design saved to Firestore: ${designId}`);
    console.log(`   Original image: ${designData.imagePath}`);
    console.log(`   Transformed image: ${designData.transformedImagePath}`);
    console.log(`   Position (% from center): x=${designData.position.x}%, y=${designData.position.y}%`);
    console.log(`   Scale (% of printable area): ${designData.scale}%`);
    console.log(`   Print type: ${designData.printType}`);
    
    return designId;
  } catch (err) {
    console.error('❌ Failed to save design to Firestore:', err);
    throw new Error(`Failed to save design: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
};

/**
 * Generate unique session ID for visitor tracking
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate unique visitor ID for tracking
 */
export const generateVisitorId = (): string => {
  return `visitor_${crypto.randomUUID()}`;
};

/**
 * Load or create visitor/session IDs from localStorage
 */
export const initializeVisitorSession = (): { visitorId: string; sessionId: string } => {
  let visitorId = localStorage.getItem('veritas_visitor_id');
  if (!visitorId) {
    visitorId = generateVisitorId();
    localStorage.setItem('veritas_visitor_id', visitorId);
  }

  let sessionId = localStorage.getItem('veritas_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem('veritas_session_id', sessionId);
  }

  return { visitorId, sessionId };
};

/**
 * Calculate max zoom based on image and zone dimensions
 * Ensures logo doesn't exceed red zone boundary
 */
export const calculateMaxZoom = (
  logoWidth: number,
  logoHeight: number,
  zoneWidth: number,
  zoneHeight: number
): number => {
  if (logoWidth === 0 || logoHeight === 0) {
    return 1.0; // Default if dimensions invalid
  }

  // Calculate scale to fit logo inside zone
  const scaleX = zoneWidth / logoWidth;
  const scaleY = zoneHeight / logoHeight;

  // Use minimum to ensure fit in both dimensions
  const maxScale = Math.min(scaleX, scaleY);

  // Clamp between 0.1 (min) and 1.0 (max)
  return Math.max(0.1, Math.min(1.0, maxScale));
};

/**
 * Validate position is within safe zone
 */
export const validatePosition = (
  position: { x: number; y: number },
  logoWidth: number,
  logoHeight: number,
  zoneX: number,
  zoneY: number,
  zoneWidth: number,
  zoneHeight: number,
  scale: number
): boolean => {
  const scaledWidth = logoWidth * scale;
  const scaledHeight = logoHeight * scale;

  const logoLeft = position.x;
  const logoRight = position.x + scaledWidth;
  const logoTop = position.y;
  const logoBottom = position.y + scaledHeight;

  const zoneRight = zoneX + zoneWidth;
  const zoneBottom = zoneY + zoneHeight;

  return (
    logoLeft >= zoneX &&
    logoRight <= zoneRight &&
    logoTop >= zoneY &&
    logoBottom <= zoneBottom
  );
};

/**
 * Convert design to shareable format (for history/export)
 */
export const exportDesignData = (designId: string, designOptions: any, mockupPath: string) => {
  return {
    designId,
    exportedAt: new Date().toISOString(),
    design: {
      ...designOptions,
      mockupPath,
    },
  };
};
